import {
  Avatar,
  Box,
  Button,
  IconButton,
  Card,
  CardContent,
  Divider,
  CardActions,
  TablePagination,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import { Search } from "@mui/icons-material"
import { format } from "date-fns";
import FileSaver from 'file-saver'
import { observer } from "mobx-react";
import { FC, useCallback, useEffect, useState } from "react";
import defaultListStore from "src/framework/store/DefaultListStore";
import LeoAvatar from './assets/images/Leo.png'
import NinaAvatar from './assets/images/Nina.png'
import { FilterDrawer } from "./containers/FilterDrawer";
import theme from "src/theme/light";
import { LocalStorage } from "src/framework/commons/LocalStorage";
import { PermissionUtil } from "src/framework/commons/PermissionUtil";
import { resolveProtocolUrl } from "src/store/global.store";

export type CategoriaBiblioteca = {
  id: number;
  nome: string;
  ordem: number;
}

export type PaginatedBibliotecaRow = {
  id: number;
  titulo: string;
  url_externa?: string;
  texto?: string;
  url?: string;
  cliques: number;
  ativo: boolean;
  segmento: string;
  created_at: string;
  categoria_biblioteca_id: number;
  comunicador?: "leo" | "nina"
}

export type PaginatedBiblioteca = {
  rows: PaginatedBibliotecaRow[],
  infoRows: {
    count: number;
    page: number;
    pageCount: number
  }
}

export interface HandleFiltersParams {
  categoria_ids?: number[];
  page?: number;
  pageSize?: number;
  segmentos?: string[]
  search?: string
}

export interface FilterProps {
  search: string;
  categoria_ids: number[];
  segmentos: string[]
}

const DEFAULT_PAGE_SIZE = 10
const DEFAULT_PAGE = 0
export const INITIAL_FILTERS_STATE = {
  search: '',
  categoria_ids: [],
  segmentos: [],
}

const download = (url: string | undefined, fileName: string) => {
  if (!url) return;
  const name: string[] = url.split(".");
  const extension: string | undefined = name.pop()?.replace(/\?r\=\d{1,}/, "");

  if (extension) {
    FileSaver.saveAs(url, `${fileName}.${extension}`)
  }
}

const getComunicadorAvatar = (comunicador: "leo" | "nina" | undefined) => {
  if(comunicador === "leo") return LeoAvatar
  return NinaAvatar
}

const useStyles = makeStyles({
  buttonRoot: {
    padding: 0
  },
  defaultBotaoCategoria: {
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
      color: 'white'
    }
  },
  selectedBotaoCategoria: {
    backgroundColor: theme.palette.primary.main,
    color:  'white',
  },
  unselectedBotaoCategoria: {
    backgroundColor: theme.palette.grey[300],
    color: theme.palette.grey[600],
  },
  paginationToolbarRoot: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center'
  },
  paginationRoot: {
    display: 'flex',
    flexWrap: 'wrap',
  }
})

const ListagemBibliotecas: FC = observer(() => {
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const classes = useStyles();
  const permissoes = PermissionUtil.getPermissions();

  const [filterBarIsOpened, setFilterBarIsOpened] = useState(false)
  const [filters, setFilters] = useState<FilterProps>(INITIAL_FILTERS_STATE)

  const [page, setPage] = useState(DEFAULT_PAGE)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  const _upload = LocalStorage.get("solar.uploadInfo", true);

  const handleFilter = useCallback((params: HandleFiltersParams = {})=>{
    const { categoria_ids = [], page = DEFAULT_PAGE, pageSize = DEFAULT_PAGE_SIZE, search, segmentos = [] } = params;
    defaultListStore?.getBibliotecas({ categoria_ids, page: page + 1, pageSize, segmentos, search })
  },[])

  const getFileName = useCallback((biblioteca: PaginatedBibliotecaRow,) => {
    if (!defaultListStore?.categoriasBibliotecas.length) return '';
    const categoriaName = defaultListStore?.categoriasBibliotecas.find(item => item.id === biblioteca.categoria_biblioteca_id)?.nome
    const filename = biblioteca.titulo
    return `${categoriaName}_${filename}`
  }, [])
  

  useEffect(()=>{
    defaultListStore?.getCategoriasBibliotecas()
    handleFilter()
  }, [handleFilter])

  const handleSearch = useCallback(()=>{
    handleFilter({
      categoria_ids: filters.categoria_ids,
      segmentos: filters.segmentos,
      page: DEFAULT_PAGE,
      pageSize,
      search: filters.search,
    })
    setFilterBarIsOpened(false)
  },[handleFilter, filters, pageSize])

  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
    handleFilter({
      categoria_ids: filters.categoria_ids,
      page: newPage,
      pageSize: pageSize,
      search: filters.search,
      segmentos: filters.segmentos
    })
  },[filters, pageSize, handleFilter])

  const handleChangePageSize = useCallback((event) => {
    const pSize = parseInt(event.target.value, 10)
    setPageSize(pSize);
    setPage(0);
    handleFilter({
      categoria_ids: filters.categoria_ids,
      page: 0,
      pageSize: pSize,
      search: filters.search,
      segmentos: filters.segmentos
    })
  },[handleFilter, filters])

  const isActived = useCallback((id: number) => {
    const ids = filters.categoria_ids || []
    if (ids.length === 0 && id === 0) return true;
    return ids.includes(id)
  }, [filters.categoria_ids])

  const CategoriasContent = useCallback(()=>{
    return (
      <>
        <Button
          onClick={() => {
            handleFilter()
            setFilters(old => ({...old, categoria_ids: []}))
          }}
          className={`${classes.defaultBotaoCategoria} ${isActived(0) ? classes.selectedBotaoCategoria : classes.unselectedBotaoCategoria}`}
          size={isMobile ? 'small' : undefined}
          style={{ marginRight: isMobile ? 8 : undefined, marginBottom: isMobile ? 8 : undefined }}
          variant="contained">Todos
        </Button>
          
        {defaultListStore?.categoriasBibliotecas?.map((categoria) => (
          <Button 
            size={isMobile ? 'small' : undefined}
            style={{ marginRight: isMobile ? 8 : undefined, marginBottom: isMobile ? 8 : undefined }}
            onClick={() => {
              handleFilter({categoria_ids: [categoria.id]})
              setFilters(old => ({...old, categoria_ids: [categoria.id]}))
            }}
            className={`${classes.defaultBotaoCategoria} ${isActived(categoria.id) ? classes.selectedBotaoCategoria : classes.unselectedBotaoCategoria}`}
            variant="contained">{categoria.nome}
          </Button>
        ))}
      </>
    )
  },[isActived, handleFilter, setFilters, isMobile, defaultListStore?.categoriasBibliotecas])

  return (
    <>
      <Box width={"100%"} maxWidth={820} margin="0 auto">
        <Box mb={"50px"} width={'100%'} display="flex" justifyContent={"space-between"}>
        {isMobile ? (
          <Box style={{ display: "flex", flexWrap: "wrap", flexDirection: "row", marginTop: 25}}>
            <CategoriasContent />
          </Box>) : (
            <CategoriasContent /> 
          )}
          <Tooltip title='Filtros'>
            <IconButton onClick={() => setFilterBarIsOpened(true)} size="large">
              <Search fontSize="medium" />
            </IconButton>
          </Tooltip>
        </Box>

        {defaultListStore?.bibliotecas?.rows?.map(biblioteca => {
          return (
            <Card style={{marginBottom: 20}}>
            <CardContent>
              <span style={{textAlign: 'left', display: 'block', fontSize: isMobile ? 18 : 22, fontWeight: 500}} >{biblioteca.titulo}</span>
              <Divider style={{margin: '10px 0'}} />
              <Box display={"flex"} alignItems="center">
                <Avatar src={getComunicadorAvatar(biblioteca.comunicador)}/>
                <span style={{marginLeft: 10, fontSize: isMobile ? 14 : 20}} >{format(new Date(biblioteca.created_at), "dd/MM/yyyy 'às' HH:mm")}</span>
              </Box>
    
              {biblioteca.texto && (
                <span style={{
                  fontWeight: 400,
                  marginTop: 10,
                  textAlign: 'left',
                  display: 'block', 
                  fontSize: 16,
                  color: 'rgba(0, 0, 0, 0.6)'
                }} dangerouslySetInnerHTML={{__html: biblioteca.texto}}/>
              )}
    
              {(biblioteca.url_externa || biblioteca.url) && <Divider style={{marginTop: 10}} />}
    
            </CardContent>
    
            {(biblioteca.url_externa || biblioteca.url) && (
              <CardActions style={{ paddingTop: 0, paddingLeft: 16 }}>
                {biblioteca.url_externa && (
                  <Button
                    className={classes.buttonRoot}
                    onClick={() => {
                      defaultListStore.incrementClick(biblioteca.id)
                      window.open(resolveProtocolUrl(biblioteca.url_externa), "_blank")
                    }} 
                    color='secondary'>
                      VISUALIZAR
                  </Button>
                )}
                {biblioteca.url && (
                  <Button
                    className={classes.buttonRoot}
                    onClick={() => {
                      if(PermissionUtil.can(PermissionUtil.PERMISSION_PROJETO_BIBLIOTECAS_ESTATISTICAS_MANUTENCAO, permissoes)) {
                        defaultListStore.incrementClick(biblioteca.id)
                      }
                      download(resolveProtocolUrl(`//${_upload?.data?.url}${biblioteca.url}`), getFileName(biblioteca))
                    }}
                    color='secondary'>
                      VISUALIZAR
                  </Button>
                )}
              </CardActions>
            )}
          </Card>
          )
        })}

        <TablePagination
          component="div"
          style={{
            display: 'flex',
            justifyContent: 'center',
          }}
          classes={isMobile ? { toolbar: classes.paginationToolbarRoot, root: classes.paginationRoot } : {}}
          count={defaultListStore?.bibliotecas?.infoRows?.count || 0}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={pageSize}
          onRowsPerPageChange={handleChangePageSize}
          labelRowsPerPage={"Registros por página:"}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count} registros`}
        />
      </Box>
      <FilterDrawer
        close={() => setFilterBarIsOpened(false)}
        isOpened={filterBarIsOpened}
        filters={filters} 
        setFilters={setFilters}
        handleSearch={handleSearch}
      />
    </>
  );
})
export { ListagemBibliotecas } 